// Scenario Comparison — PSD §7.2 (F7.2). Analysis tab.

import { useMemo } from 'react';
import type { ValuationInputs } from '@backsolve/shared-types';
import { runScenario, defaultPresets, type ScenarioDefinition } from '@backsolve/shared-logic/scenarios';

interface ScenarioCompareProps {
  inputs: ValuationInputs;
}

export function ScenarioCompare({ inputs }: ScenarioCompareProps) {
  const presets = useMemo(() => defaultPresets(inputs), [inputs]);
  const results = useMemo(
    () => presets.map((s) => runScenario(inputs, s)),
    [inputs, presets],
  );

  return (
    <div className="scenario-compare">
      <h3>Scenario Comparison</h3>
      <p className="result-caption">
        Re-runs the valuation against built-in scenario presets that override
        individual inputs. Use this to bound FMV across plausible assumption
        sets; the active valuation is never modified.
      </p>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Metric</th>
              {presets.map((s) => (
                <th key={s.id}>{s.name}</th>
              ))}
            </tr>
          </thead>
        <tbody>
          <tr>
            <td>Equity Value Override</td>
            {presets.map((s) => (
              <td key={s.id}>{s.equityValueOverride ? `$${(s.equityValueOverride / 1e6).toFixed(1)}M` : '—'}</td>
            ))}
          </tr>
          <tr>
            <td>Volatility</td>
            {presets.map((s) => (
              <td key={s.id}>
                {s.volatilityOverride ? `${(s.volatilityOverride * 100).toFixed(0)}%` : '—'}
              </td>
            ))}
          </tr>
          <tr>
            <td>Time to Liquidity</td>
            {presets.map((s) => (
              <td key={s.id}>
                {s.timeToLiquidityOverride ? `${s.timeToLiquidityOverride.toFixed(1)}y` : '—'}
              </td>
            ))}
          </tr>
          <tr>
            <td>DLOM %</td>
            {presets.map((s) => (
              <td key={s.id}>{s.dlomOverride ? `${(s.dlomOverride * 100).toFixed(0)}%` : '—'}</td>
            ))}
          </tr>
          <tr className="row-divider">
            <td>
              <strong>FMV / share</strong>
            </td>
            {results.map((r, i) => (
              <td key={i}>
                {r.result ? (
                  <strong>${r.result.headline.commonFmvPerShare.toFixed(4)}</strong>
                ) : (
                  <span className="badge badge-error">Error</span>
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td>Recommended Strike</td>
            {results.map((r, i) => (
              <td key={i}>
                {r.result ? `$${r.result.headline.recommendedStrikePrice.toFixed(2)}` : '—'}
              </td>
            ))}
          </tr>
          <tr>
            <td>Common Value (post-DLOM)</td>
            {results.map((r, i) => (
              <td key={i}>
                {r.result ? `$${r.result.headline.commonValuePostDlom.toLocaleString()}` : '—'}
              </td>
            ))}
          </tr>
          {results.some((r) => r.error) && (
            <tr>
              <td>Errors</td>
              {results.map((r, i) => (
                <td key={i} className="badge-error">
                  {r.error ?? ''}
                </td>
              ))}
            </tr>
          )}
        </tbody>
        </table>
      </div>
    </div>
  );
}

export type { ScenarioDefinition };
